import { useEffect, useState } from 'react';
import { useClickOutside } from '@mantine/hooks';
import Image from 'next/image';
import classNames from 'classnames';
import { isBoolean } from 'lodash';

import classes from './FilterGroup.module.css';
import { GroupOption } from 'utils/interfaces/commonInterfacesAndTypes';
import Chevron from 'static/icons/chevron.svg';
import Checkbox from '../Checkbox/Checkbox';
import { FilledButton, OutlinedButton } from 'components/common/Button/Button';
import ArrowIcon from 'static/icons/arrow_without_tail.svg';
import MobileSubOptionsFilter from './MobileSubOptionsFilter/MobileSubOptionsFilter';
import useTranslation from 'next-translate/useTranslation';

type FitlerGroupProps = {
   selectedMainOptions: string[];
   selectedSubOptions: { [key: string]: string[] };
   options: GroupOption[];
   placeholder: string;
   onChangeMainOptionHandler: (value: string) => void;
   onChangeSubOptionHandler: (mainOption: string, value: string) => void;
   onSubmitHandler?: () => void;
   onClearMainOptionsHandler?: () => void;
   onClearSubOptionsHandler?: (mainOption: string) => void;
   onClickToggleHandler?: (filterName: string, openValue: boolean) => void;
   onCloseMenuHandler?: () => void;
   filterGroupName?: string;
   isFilterGroupOpen?: boolean;
   mobileMode?: boolean;
   closeOnClickOutside?: boolean;
   className?: string;
};

const FitlerGroup = ({
   selectedMainOptions,
   selectedSubOptions,
   options,
   onChangeMainOptionHandler,
   onChangeSubOptionHandler,
   placeholder,
   onClearMainOptionsHandler,
   onClearSubOptionsHandler,
   onSubmitHandler,
   onClickToggleHandler,
   onCloseMenuHandler,
   filterGroupName,
   isFilterGroupOpen,
   mobileMode,
   closeOnClickOutside = true,
   className,
}: FitlerGroupProps) => {
   const [isOpen, setIsOpen] = useState(false);
   const ref = useClickOutside(() => setIsOpen(false));
   const [selectedMobileSubOptionsFilter, setSelectedMobileSubOptionsFilter] = useState<
      string | null
   >(null);
   const { t } = useTranslation('common');

   useEffect(() => {
      isBoolean(isFilterGroupOpen) && setIsOpen(isFilterGroupOpen);
   }, [isFilterGroupOpen]);

   const clearMainOptionsHandler = () => {
      setIsOpen(false);
      onClearMainOptionsHandler && onClearMainOptionsHandler();
   };

   const clearSubOptionsHandler = (mainOption: string) => () =>
      onClearSubOptionsHandler && onClearSubOptionsHandler(mainOption);

   const submitFilterHandler = () => {
      setIsOpen(false);
      onSubmitHandler && onSubmitHandler();
   };

   const selectMobileSubOptionsFilterHandler = (mainOption: string) => () =>
      setSelectedMobileSubOptionsFilter(mainOption);

   const clickBackHandler = () => setSelectedMobileSubOptionsFilter(null);

   const clickToggleHandler = (isOpenValue: boolean) => {
      onClickToggleHandler && filterGroupName
         ? onClickToggleHandler(filterGroupName, isOpenValue)
         : setIsOpen(isOpenValue);
   };

   return (
      <div
         ref={closeOnClickOutside ? ref : undefined}
         className={classNames(
            classes.container,
            { [classes.expandedInPlace]: mobileMode, [classes.active]: isOpen },
            className,
         )}
      >
         <div
            {...(!isOpen && {
               onClick: () => clickToggleHandler(true),
            })}
            className={classes.select}
         >
            {placeholder}
            {selectedMainOptions.length > 0 && (
               <div className={classes.counter}>{selectedMainOptions.length}</div>
            )}

            <div
               onClick={() => clickToggleHandler(!isOpen)}
               className={classNames(classes.iconContainer, {
                  [classes.iconContainerActive]: isOpen,
               })}
            >
               <Image src={Chevron} alt="Chevron" />
            </div>
         </div>
         <div className={classNames(classes.dropdown, { [classes.active]: isOpen })}>
            {options.map(option => (
               <div key={option.value} className={classes.optionGroup}>
                  <div
                     {...(!mobileMode && {
                        onClick: () => onChangeMainOptionHandler(option.value),
                     })}
                     className={classes.option}
                  >
                     <Checkbox
                        id={option.value}
                        label={
                           t(option.label) +
                           (selectedSubOptions[option.value] &&
                           selectedSubOptions[option.value].length
                              ? ` (${selectedSubOptions[option.value].length})`
                              : '')
                        }
                        value={selectedMainOptions.includes(option.value)}
                        className={classes.mobileCheckbox}
                        readonly
                        {...(mobileMode && {
                           onClick: () => onChangeMainOptionHandler(option.value),
                        })}
                     />
                     {!mobileMode && option.subOptions && (
                        <span className={classes.arrow}>
                           <Image src={ArrowIcon} layout="responsive" alt="" />
                        </span>
                     )}
                     {mobileMode && option.subOptions && (
                        <div
                           onClick={selectMobileSubOptionsFilterHandler(option.value)}
                           className={classNames(classes.iconContainer, classes.subOptionsArrowBtn)}
                        >
                           <Image src={Chevron} alt="Chevron" />
                        </div>
                     )}
                  </div>
                  {!mobileMode && option.subOptions && (
                     <div className={classNames(classes.subOptionsDropdown)}>
                        <div className={classes.subOptionsContainer}>
                           {option.subOptions.map(subOption => (
                              <div
                                 onClick={() =>
                                    onChangeSubOptionHandler(option.value, subOption.value)
                                 }
                                 className={classes.option}
                                 key={subOption.value}
                              >
                                 <Checkbox
                                    id={subOption.value}
                                    label={subOption.label}
                                    value={selectedSubOptions[option.value]?.includes(
                                       subOption.value,
                                    )}
                                    readonly
                                 />
                              </div>
                           ))}
                        </div>
                        {!mobileMode && (
                           <div className={classes.buttonsSection}>
                              <FilledButton
                                 type="button"
                                 onClick={submitFilterHandler}
                                 content={t('apply_filters')}
                                 className={classes.btn}
                              />
                              <OutlinedButton
                                 type="button"
                                 onClick={clearSubOptionsHandler(option.value)}
                                 content={t('clear')}
                                 className={classes.btn}
                              />
                           </div>
                        )}
                     </div>
                  )}
                  {selectedMobileSubOptionsFilter === option.value && option.subOptions && (
                     <MobileSubOptionsFilter
                        mainOption={option}
                        subOptions={option.subOptions}
                        selectedSubOptions={selectedSubOptions}
                        onClickBackHandler={clickBackHandler}
                        onCloseMenuHandler={onCloseMenuHandler}
                        onChangeSubOptionHandler={onChangeSubOptionHandler}
                     />
                  )}
               </div>
            ))}
            {!mobileMode && (
               <div className={classes.buttonsSection}>
                  <FilledButton
                     type="button"
                     onClick={submitFilterHandler}
                     content={t('apply_filters')}
                     className={classes.btn}
                  />
                  <OutlinedButton
                     type="button"
                     onClick={clearMainOptionsHandler}
                     content={t('clear')}
                     className={classes.btn}
                  />
               </div>
            )}
         </div>
      </div>
   );
};

export default FitlerGroup;
